import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Layout from '../components/common/Layout';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import BuscadorSelect from '../components/common/BuscadorSelect';
import { rrhhInventarioService } from '../services/rrhhInventario';
import toast from 'react-hot-toast';
import {
    ArrowLeftRight, ArrowDownCircle, ArrowUpCircle, Plus, X, FileDown, FileCheck2, Upload, AlertTriangle, Trash2,
} from 'lucide-react';

// ─── Modal: registrar movimiento ────────────────────────────────────────────
// Entrada = reposición de un producto. Salida = una "entrega" a un empleado,
// que puede incluir varios productos y se respalda con un solo Formato EPP.

const campoCls = 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';
const inputCls = `w-full ${campoCls}`;

// Motivo de la entrega (por qué se entrega) — pedido de RRHH para distinguir
// altas de bajas por desgaste/extravío. Solo aplica a salidas.
const MOTIVOS_ENTREGA = [
    { value: 'ingreso_nuevo', label: 'Ingreso nuevo' },
    { value: 'perdida', label: 'Pérdida' },
    { value: 'renovacion', label: 'Renovación' },
];
const MOTIVO_ENTREGA_LABEL = Object.fromEntries(MOTIVOS_ENTREGA.map((m) => [m.value, m.label]));

const ModalMovimiento = ({ items, empleados, onCerrar, onGuardado }) => {
    // Se escribe para buscar (SKU, descripción, talla, categoría) en vez de
    // recorrer la lista; la talla se ve sin tener que volver al Catálogo.
    const opcionesItems = useMemo(() => items.map((i) => ({
        value: i.id,
        label: `${i.sku ? `${i.sku} — ` : ''}${i.descripcion}${i.talla ? ` · Talla ${i.talla}` : ''}`,
        detalle: [`Stock: ${i.stock_actual}`, i.categoria].filter(Boolean).join(' · '),
        busqueda: `${i.sku || ''} ${i.talla ? `talla ${i.talla}` : ''} ${i.categoria || ''}`,
    })), [items]);
    const opcionesEmpleados = useMemo(() => empleados.map((e) => ({
        value: e.id,
        label: `${e.nombre_completo}${e.cargo ? ` — ${e.cargo}` : ''}`,
        detalle: e.documento ? `DNI ${e.documento}` : undefined,
        busqueda: e.documento || '',
    })), [empleados]);
    const [tipo, setTipo] = useState('salida');
    const [lineas, setLineas] = useState([{ itemId: '', cantidad: 1 }]);
    const [empleadoId, setEmpleadoId] = useState('');
    const [motivoEntrega, setMotivoEntrega] = useState('');
    const [motivo, setMotivo] = useState('');
    const [formatoFirmado, setFormatoFirmado] = useState(null);
    const [guardando, setGuardando] = useState(false);

    const actualizarLinea = (idx, campo, valor) =>
        setLineas((prev) => prev.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));

    const cambiarTipo = (nuevo) => {
        setTipo(nuevo);
        if (nuevo === 'entrada') {
            setLineas((prev) => prev.slice(0, 1));
            setMotivoEntrega('');
            setFormatoFirmado(null);
        }
    };

    const handleGuardar = async () => {
        if (lineas.some((l) => !l.itemId)) { toast.error('Selecciona el producto de cada línea'); return; }
        if (lineas.some((l) => !l.cantidad || Number(l.cantidad) <= 0)) { toast.error('La cantidad debe ser mayor a 0'); return; }
        if (tipo === 'salida' && !empleadoId) { toast.error('Selecciona el empleado que recibe el producto'); return; }
        if (tipo === 'salida' && !motivoEntrega) { toast.error('Selecciona el motivo de la entrega'); return; }

        setGuardando(true);
        try {
            if (tipo === 'entrada') {
                await rrhhInventarioService.registrarMovimiento({
                    item_id: Number(lineas[0].itemId),
                    tipo: 'entrada',
                    cantidad: Number(lineas[0].cantidad),
                    empleado_id: null,
                    motivo: motivo || null,
                });
                toast.success('Entrada registrada');
                onGuardado(null);
            } else {
                const entrega = await rrhhInventarioService.registrarEntrega({
                    empleado_id: Number(empleadoId),
                    items: lineas.map((l) => ({ item_id: Number(l.itemId), cantidad: Number(l.cantidad) })),
                    motivo_entrega: motivoEntrega,
                    motivo: motivo || null,
                });

                // Si ya tenían el Formato EPP firmado a mano (ej. una entrega hecha en
                // papel que están registrando después), se adjunta en el mismo paso en
                // vez de tener que volver a buscar la entrega para subirlo.
                if (formatoFirmado) {
                    try {
                        await rrhhInventarioService.subirFormatoFirmado(entrega.entrega_codigo, formatoFirmado);
                        toast.success(`Entrega ${entrega.entrega_codigo} registrada con el Formato EPP adjunto`);
                    } catch (errFormato) {
                        toast.error(
                            errFormato?.response?.data?.detail
                                || `Entrega ${entrega.entrega_codigo} registrada, pero no se pudo adjuntar el Formato EPP. Subilo desde la tabla.`
                        );
                    }
                } else {
                    toast.success(`Entrega ${entrega.entrega_codigo} registrada`);
                }
                onGuardado(entrega.entrega_codigo);
            }
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al registrar el movimiento');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal title="Nuevo movimiento" onClose={onCerrar} size="md">
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => cambiarTipo('entrada')}
                        className={`flex items-center justify-center py-2 rounded-lg text-sm font-semibold border ${
                            tipo === 'entrada' ? 'bg-green-50 border-green-400 text-green-700' : 'border-gray-300 text-gray-500'
                        }`}
                    >
                        <ArrowDownCircle className="h-4 w-4 mr-1.5" /> Entrada
                    </button>
                    <button
                        onClick={() => cambiarTipo('salida')}
                        className={`flex items-center justify-center py-2 rounded-lg text-sm font-semibold border ${
                            tipo === 'salida' ? 'bg-red-50 border-red-400 text-red-700' : 'border-gray-300 text-gray-500'
                        }`}
                    >
                        <ArrowUpCircle className="h-4 w-4 mr-1.5" /> Salida (entrega)
                    </button>
                </div>

                {tipo === 'salida' && (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Empleado que recibe</label>
                        <BuscadorSelect
                            opciones={opcionesEmpleados}
                            value={empleadoId}
                            onChange={setEmpleadoId}
                            placeholder="Escribe nombre o DNI..."
                        />
                    </div>
                )}

                {tipo === 'salida' && (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Motivo de la entrega</label>
                        <select value={motivoEntrega} onChange={(e) => setMotivoEntrega(e.target.value)} className={inputCls}>
                            <option value="">Seleccionar motivo...</option>
                            {MOTIVOS_ENTREGA.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {tipo === 'salida' ? 'Productos entregados' : 'Producto'}
                    </label>
                    <div className="space-y-2">
                        {lineas.map((linea, idx) => {
                            const item = items.find((i) => i.id === Number(linea.itemId));
                            return (
                                <div key={idx}>
                                    <div className="grid grid-cols-[minmax(0,1fr)_5rem_1rem] items-center gap-2">
                                        <BuscadorSelect
                                            opciones={opcionesItems}
                                            value={linea.itemId}
                                            onChange={(v) => actualizarLinea(idx, 'itemId', v)}
                                            placeholder="Escribe producto, SKU o talla..."
                                        />
                                        <input
                                            type="number"
                                            min="1"
                                            value={linea.cantidad}
                                            onChange={(e) => actualizarLinea(idx, 'cantidad', e.target.value)}
                                            className={campoCls}
                                            title="Cantidad"
                                        />
                                        {lineas.length > 1 && (
                                            <button
                                                onClick={() => setLineas((prev) => prev.filter((_, i) => i !== idx))}
                                                className="text-gray-400 hover:text-red-600"
                                                title="Quitar"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    {tipo === 'salida' && item && Number(linea.cantidad) > item.stock_actual && (
                                        <p className="text-xs text-red-500 mt-1">Solo quedan {item.stock_actual} {item.unidad}(s)</p>
                                    )}
                                    {item && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Stock: {item.stock_actual} {item.unidad}(s){item.talla ? ` · Talla ${item.talla}` : ''}
                                        </p>
                                    )}
                                    {tipo === 'salida' && item?.vida_util_dias && (
                                        <p className="text-xs text-gray-400 mt-1">Caduca a los {item.vida_util_dias} días de entregado</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {tipo === 'salida' && (
                        <button
                            onClick={() => setLineas((prev) => [...prev, { itemId: '', cantidad: 1 }])}
                            className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1" /> Agregar otro producto
                        </button>
                    )}
                </div>

                {tipo === 'salida' && (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Formato EPP firmado (opcional)
                        </label>
                        <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                            onChange={(e) => setFormatoFirmado(e.target.files?.[0] || null)}
                            className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            Si ya tenés el formato firmado a mano, adjuntalo ahora. Si no, podés generar el PDF en
                            blanco para imprimir y subir el escaneo después, desde la tabla de movimientos.
                        </p>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Motivo / notas (opcional)</label>
                    <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} className={inputCls} />
                </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancelar
                </button>
                <Button onClick={handleGuardar} loading={guardando} disabled={guardando}>
                    Registrar
                </Button>
            </div>
        </Modal>
    );
};

// ─── Formato EPP (SUNAFIL) de una entrega: descargar PDF / subir firmado ────

const AccionesFormato = ({ entregaCodigo, formatoUrl, onSubido }) => {
    const inputRef = useRef(null);
    const [descargando, setDescargando] = useState(false);
    const [subiendo, setSubiendo] = useState(false);

    const descargar = async () => {
        setDescargando(true);
        try {
            await rrhhInventarioService.descargarFormatoEpp(entregaCodigo);
        } catch (err) {
            toast.error('No se pudo generar el formato');
        } finally {
            setDescargando(false);
        }
    };

    const subir = async (e) => {
        const archivo = e.target.files?.[0];
        e.target.value = '';
        if (!archivo) return;
        setSubiendo(true);
        try {
            await rrhhInventarioService.subirFormatoFirmado(entregaCodigo, archivo);
            toast.success('Formato firmado guardado');
            onSubido();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'No se pudo subir el formato');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <div className="flex items-center gap-2 whitespace-nowrap">
            <button onClick={descargar} disabled={descargando} className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50" title="Descargar formato para firmar">
                <FileDown className="h-4 w-4 mr-0.5" /> PDF
            </button>
            {formatoUrl ? (
                <a href={formatoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-green-700 hover:text-green-900" title="Ver formato firmado">
                    <FileCheck2 className="h-4 w-4 mr-0.5" /> Firmado
                </a>
            ) : (
                <span className="inline-flex items-center text-xs text-amber-600" title="Falta subir el formato firmado">
                    <AlertTriangle className="h-4 w-4 mr-0.5" /> Sin firma
                </span>
            )}
            <button onClick={() => inputRef.current?.click()} disabled={subiendo} className="inline-flex items-center text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50" title={formatoUrl ? 'Reemplazar formato firmado' : 'Subir formato firmado (PDF o foto)'}>
                <Upload className="h-4 w-4" />
            </button>
            {/* globals.css fuerza display:block en todo input[type=file]: se oculta con el contenedor */}
            <span className="sr-only">
                <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" tabIndex={-1} onChange={subir} />
            </span>
        </div>
    );
};

const formatearFecha = (iso) => {
    if (!iso) return '—';
    const [a, m, d] = iso.split('-');
    return `${d}/${m}/${a}`;
};

// ─── Página principal ───────────────────────────────────────────────────────

const RRHHMovimientosPage = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [items, setItems] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroTipo, setFiltroTipo] = useState('');
    const [busquedaDni, setBusquedaDni] = useState('');
    const [filtroDni, setFiltroDni] = useState('');
    const [eliminandoId, setEliminandoId] = useState(null);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [entregaReciente, setEntregaReciente] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [movs, its, emps] = await Promise.all([
                rrhhInventarioService.listarMovimientos({
                    ...(filtroTipo ? { tipo: filtroTipo } : {}),
                    ...(filtroDni ? { documento: filtroDni } : {}),
                }),
                rrhhInventarioService.listarItems(),
                rrhhInventarioService.listarEmpleados(),
            ]);
            setMovimientos(movs);
            setItems(its);
            setEmpleados(emps);
        } catch (err) {
            toast.error('Error al cargar los movimientos');
        } finally {
            setLoading(false);
        }
    }, [filtroTipo, filtroDni]);

    useEffect(() => { cargar(); }, [cargar]);

    // Espera a que terminen de teclear el DNI antes de consultar.
    useEffect(() => {
        const t = setTimeout(() => setFiltroDni(busquedaDni.trim()), 400);
        return () => clearTimeout(t);
    }, [busquedaDni]);

    const eliminar = async (m) => {
        const efecto = m.tipo === 'salida'
            ? `Se devolverán ${m.cantidad} unidad(es) de "${m.item_descripcion}" al stock.`
            : `Se descontarán ${m.cantidad} unidad(es) de "${m.item_descripcion}" del stock.`;
        if (!window.confirm(`¿Eliminar esta ${m.tipo}? ${efecto}`)) return;
        setEliminandoId(m.id);
        try {
            const res = await rrhhInventarioService.eliminarMovimiento(m.id);
            toast.success(`Movimiento eliminado. Stock actual: ${res.stock_actual}`);
            cargar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'No se pudo eliminar el movimiento');
        } finally {
            setEliminandoId(null);
        }
    };

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="bg-indigo-100 p-2.5 rounded-xl">
                            <ArrowLeftRight className="h-6 w-6 text-indigo-700" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">RRHH · Movimientos</h1>
                            <p className="text-sm text-gray-500">Entradas (reposición) y entregas a empleados con su Formato EPP (SUNAFIL)</p>
                        </div>
                    </div>
                    <Button onClick={() => setModalAbierto(true)}>
                        <Plus className="h-4 w-4 inline mr-1" /> Nuevo movimiento
                    </Button>
                </div>

                {entregaReciente && (
                    <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm">
                        <span className="text-blue-800">
                            Entrega <strong>{entregaReciente}</strong> registrada. Imprime el Formato EPP, hazlo firmar al trabajador y súbelo escaneado.
                        </span>
                        <div className="flex items-center gap-3">
                            <AccionesFormato entregaCodigo={entregaReciente} formatoUrl={null} onSubido={() => { setEntregaReciente(null); cargar(); }} />
                            <button onClick={() => setEntregaReciente(null)} className="text-blue-400 hover:text-blue-700" title="Cerrar"><X className="h-4 w-4" /></button>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
                    <select
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        <option value="">Todos los movimientos</option>
                        <option value="entrada">Solo entradas</option>
                        <option value="salida">Solo salidas</option>
                    </select>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={busquedaDni}
                        onChange={(e) => setBusquedaDni(e.target.value)}
                        placeholder="Buscar por DNI del empleado"
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                {loading ? (
                    <Loading message="Cargando movimientos..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Tipo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Producto</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Cantidad</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Empleado</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Motivo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Vence</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Formato EPP</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Registrado por</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Fecha</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {movimientos.length === 0 && (
                                    <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                                        {filtroDni ? `Sin movimientos para el DNI "${filtroDni}"` : 'Sin movimientos'}
                                    </td></tr>
                                )}
                                {movimientos.map((m) => (
                                    <tr key={m.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            {m.tipo === 'entrada' ? (
                                                <span className="inline-flex items-center text-green-700 font-semibold">
                                                    <ArrowDownCircle className="h-4 w-4 mr-1" /> Entrada
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center text-red-700 font-semibold">
                                                    <ArrowUpCircle className="h-4 w-4 mr-1" /> Salida
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">{m.item_descripcion}</div>
                                            <div className="text-xs text-gray-400">{m.item_sku}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{m.cantidad}</td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {m.empleado_nombre || '—'}
                                            {m.empleado_documento && <div className="text-xs text-gray-500">DNI {m.empleado_documento}</div>}
                                            {m.entrega_codigo && <div className="text-xs text-gray-400">{m.entrega_codigo}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {m.motivo_entrega ? (MOTIVO_ENTREGA_LABEL[m.motivo_entrega] || m.motivo_entrega) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {m.renovado_en ? (
                                                <span className="text-xs text-gray-400" title={`Vencía ${formatearFecha(m.fecha_vencimiento)}`}>Renovado</span>
                                            ) : formatearFecha(m.fecha_vencimiento)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {m.entrega_codigo ? (
                                                <AccionesFormato entregaCodigo={m.entrega_codigo} formatoUrl={m.formato_epi_url} onSubido={cargar} />
                                            ) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{m.registrado_por}</td>
                                        <td className="px-4 py-3 text-gray-500">{new Date(m.created_at).toLocaleString('es-PE')}</td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => eliminar(m)}
                                                disabled={eliminandoId === m.id}
                                                className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                title={m.tipo === 'salida' ? 'Eliminar salida (el stock se reintegra)' : 'Eliminar entrada (el stock se descuenta)'}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modalAbierto && (
                <ModalMovimiento
                    items={items}
                    empleados={empleados}
                    onCerrar={() => setModalAbierto(false)}
                    onGuardado={(codigo) => { setEntregaReciente(codigo); cargar(); }}
                />
            )}
        </Layout>
    );
};

export default RRHHMovimientosPage;
